/* eslint-disable react-hooks/exhaustive-deps */
import { css } from 'aphrodite';
import { addWeeks, subWeeks } from 'date-fns';
import { parseAsIsoDate, parseAsString, parseAsStringEnum, useQueryState, useQueryStates } from 'nuqs';
import * as React from 'react';
import { DemoStatusFilter, IUser } from '../../../extViewmodels';
import { addUserToUserQueryCache } from '../../../queries';
import { useLeadPhoneCallOutcomesQuery } from '../../../queries/Lead/useLeadPhoneCallOutcomesQuery';
import { Button, SelectButton } from '../../../web/components-v2/Button';
import { Calendar } from '../../../web/components-v2/Calendar';
import {
	ComboboxClear,
	ComboboxContent,
	ComboboxControl,
	ComboboxInput,
	ComboboxList,
	ComboboxValue,
} from '../../../web/components-v2/Combobox';
import { Popover, PopoverContent, PopoverTrigger } from '../../../web/components/Popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../web/components/Select';
import {
	ManagerCombobox,
	ManagerComboboxStatus,
	UserCombobox,
	UserComboboxAvatarItem,
	UserComboboxStatus,
} from '../../../web/components/UserCombobox';
import { bs } from '../../../web/styles/styles';
import { FieldKey } from '../../viewModels/form';
import { styleSheet } from './styles';

const DEMO_STATUS_OPTIONS = [
	{ label: 'Demos to Confirm', value: DemoStatusFilter.DemosToConfirm },
	{ label: 'Demos to Reschedule', value: DemoStatusFilter.DemosToReschedule },
	{ label: 'Upcoming Demos', value: DemoStatusFilter.UpcomingDemos },
	{ label: 'Past Demos', value: DemoStatusFilter.PastDemos },
];

export const Filters = () => {
	const id = React.useId();
	const leadPhoneCallOutcomesQuery = useLeadPhoneCallOutcomesQuery();
	const [activeUserId, setActiveUserId] = useQueryState('userId', parseAsString);
	const [dateRange, setDateRange] = useQueryStates({
		from: parseAsIsoDate,
		to: parseAsIsoDate,
	});
	const [demoStatus, setDemoStatus] = useQueryState(
		'demoStatus',
		parseAsStringEnum<DemoStatusFilter>(Object.values(DemoStatusFilter)).withDefault(DemoStatusFilter.DemosToConfirm)
	);
	const [outcomeName, setOutcomeName] = useQueryState(
		'outcomeName',
		parseAsStringEnum<FieldKey>(Object.values(FieldKey)).withDefault(FieldKey.Unknown)
	);
	const [managerUserId, setManagerUserId] = useQueryState('managerUserId', parseAsString);
	const [demoPerformerUserId, setDemoPerformerUserId] = useQueryState('demoPerformerUserId', parseAsString);
	const [companyId, setCompanyId] = useQueryState('companyId', parseAsString);

	const handleUserChange = (user: IUser) => {
		setActiveUserId(user?.id ?? null);
		// Clear manager selection since they're mutually exclusive
		if (user?.id) {
			addUserToUserQueryCache({ id: user.id, user });
			setManagerUserId(null);
		}
	};
	const handleCloserOwnerChange = (user: IUser) => {
		setDemoPerformerUserId(user?.id || null);
	};
	const handleManagerChange = (user: IUser) => {
		setManagerUserId(user?.id ?? null);
		// Clear user selection since they're mutually exclusive
		if (user?.id) {
			setActiveUserId(null);
		}
	};
	const handleOutcomeChange = (newOutcomeName: FieldKey) => {
		setOutcomeName(newOutcomeName);
	};
	const handleClearAllFilters = (e: React.MouseEvent) => {
		e.preventDefault();

		// Clear all selected state
		setActiveUserId(null);
		setManagerUserId(null);
		setDemoPerformerUserId(null);
		setDemoStatus(null);
		setOutcomeName(null);
		setDateRange({ from: null, to: null });
		setCompanyId(null);
	};

	const handleDemoStatusChange = (status: DemoStatusFilter) => {
		if (status === DemoStatusFilter.DemosToConfirm) {
			setOutcomeName(null);
		}
		if (status === DemoStatusFilter.DemosToReschedule) {
			setDateRange({ from: subWeeks(new Date(), 1), to: new Date() });
		}
		if (status === DemoStatusFilter.UpcomingDemos) {
			const currentDate = new Date();
			setDateRange({ from: currentDate, to: addWeeks(currentDate, 1) });
		}
		if (status === DemoStatusFilter.PastDemos) {
			setManagerUserId(null);
		}
		setDemoStatus(status);
	};
	// Hide manager filter only for Past Demos
	const shouldShowManagerFilter = demoStatus !== DemoStatusFilter.PastDemos;

	// Check if any filters are active
	const hasActiveFilters =
		companyId ||
		activeUserId ||
		managerUserId ||
		demoPerformerUserId ||
		dateRange.from ||
		dateRange.to ||
		demoStatus !== DemoStatusFilter.DemosToConfirm ||
		outcomeName !== FieldKey.Unknown;

	const outcomeItems: { label: string; value: FieldKey }[] = [{ label: 'All outcome types', value: FieldKey.Unknown }];
	leadPhoneCallOutcomesQuery.data?.forEach(outcome => {
		outcomeItems.push({
			label: outcome.label,
			value: outcome.name,
		});
	});

	return (
		<div className={css(styleSheet.filter)}>
			<UserCombobox id={activeUserId} key={`sdr-${activeUserId}`} onValueChange={handleUserChange}>
				<div className={css(styleSheet.filterGroup)}>
					<label htmlFor={`${id}-sdr-combobox`} className={css(styleSheet.label)}>
						SDR
					</label>
					<ComboboxControl>
						<ComboboxValue>
							<ComboboxInput placeholder='Filter by SDR' id={`${id}-sdr-combobox`} />
						</ComboboxValue>
						<ComboboxClear />
					</ComboboxControl>
				</div>
				<ComboboxContent>
					<UserComboboxStatus />
					<ComboboxList>
						{(user: IUser) => <UserComboboxAvatarItem key={`sdr-item-${user.id}`} user={user} />}
					</ComboboxList>
				</ComboboxContent>
			</UserCombobox>
			<ManagerCombobox
				id={managerUserId}
				key={`managers-${managerUserId}`}
				onValueChange={handleManagerChange}
				disabled={!shouldShowManagerFilter}
			>
				<div className={css(bs.flex, bs.flexCol, bs.gap1)}>
					<label htmlFor={`${id}-manager-combobox`} className={css(bs.textXs, bs.fontBold)}>
						Manager
					</label>
					<ComboboxControl>
						<ComboboxValue>
							<ComboboxInput placeholder='Filter by manager' id={`${id}-manager-combobox`} />
						</ComboboxValue>
						<ComboboxClear />
					</ComboboxControl>
				</div>
				<ComboboxContent>
					<ManagerComboboxStatus />
					<ComboboxList>
						{(user: IUser) => <UserComboboxAvatarItem key={`manager-item-${user.id}`} user={user} />}
					</ComboboxList>
				</ComboboxContent>
			</ManagerCombobox>
			<UserCombobox
				id={demoPerformerUserId}
				key={`closer-owner-${demoPerformerUserId}`}
				onValueChange={handleCloserOwnerChange}
			>
				<div className={css(styleSheet.filterGroup)}>
					<label htmlFor={`${id}-closer-combobox`} className={css(styleSheet.label)}>
						Closer owner
					</label>
					<ComboboxControl>
						<ComboboxValue>
							<ComboboxInput placeholder='Filter by closer owner' id={`${id}-closer-combobox`} />
						</ComboboxValue>
						<ComboboxClear />
					</ComboboxControl>
				</div>
				<ComboboxContent>
					<UserComboboxStatus />
					<ComboboxList>
						{(user: IUser) => <UserComboboxAvatarItem key={`closer-owner-item-${user.id}`} user={user} />}
					</ComboboxList>
				</ComboboxContent>
			</UserCombobox>
			<Popover>
				<div className={css(styleSheet.filterGroup)}>
					<label htmlFor='dates' className={css(styleSheet.label)}>
						Date Range
					</label>
					<PopoverTrigger asChild>
						<SelectButton id='dates'>
							{dateRange?.from && dateRange?.to
								? `${dateRange.from.toLocaleDateString()} - ${dateRange.to.toLocaleDateString()}`
								: 'Filter by Date'}
						</SelectButton>
					</PopoverTrigger>
				</div>
				<PopoverContent styles={[bs.wAuto, bs.overflowHidden, bs.p0]} align='start'>
					<Calendar
						mode='range'
						selected={dateRange}
						numberOfMonths={2}
						timeZone='UTC'
						onSelect={r => {
							setDateRange(r);
						}}
					/>
				</PopoverContent>
			</Popover>
			<div className={css(styleSheet.filterGroup)}>
				<label htmlFor='demo-status' className={css(styleSheet.label)}>
					Demo Status
				</label>
				<Select value={demoStatus} onValueChange={handleDemoStatusChange} items={DEMO_STATUS_OPTIONS}>
					<SelectTrigger id='demo-status' styles={[bs.wFull]}>
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						{DEMO_STATUS_OPTIONS.map(option => (
							<SelectItem key={option.value} value={option.value}>
								{option.label}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</div>
			<div className={css(styleSheet.filterGroup)}>
				<label htmlFor={`${id}-outcome-select`} className={css(styleSheet.label)}>
					Outcome
				</label>
				<Select
					value={outcomeName}
					onValueChange={handleOutcomeChange}
					disabled={demoStatus !== DemoStatusFilter.DemosToConfirm}
					items={outcomeItems}
				>
					<SelectTrigger id={`${id}-outcome-select`} styles={[bs.wFull]}>
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						{outcomeItems.map(outcomeItem => (
							<SelectItem key={`${id}-outcome-${outcomeItem.value}`} value={outcomeItem.value}>
								{outcomeItem.label}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</div>
			{hasActiveFilters && (
				<Button styles={[styleSheet.clearFiltersButton]} variant='secondary' onClick={handleClearAllFilters}>
					Clear all filters
				</Button>
			)}
		</div>
	);
};
